/**
 * HARDWARE CATALOG — SUNMI card setup (owner-approved mockup board 5).
 * Spec: docs/agenticpos-rubytill/sunmi-card-setup.md §1, §2.
 *
 *   CardSetupPolicyPanel     — on a `pos_terminal` row that CARRIES a
 *                              `cardSetupPolicy`: its variants, facts, and the
 *                              PRIMARY / BACKUP policy table with defaults.
 *   ReaderCapabilitiesPanel  — on every `payment_device` row: what the reader can
 *                              do. Absent = the reader family's defaults; editing
 *                              writes the optional `readerCapabilities` field.
 *
 * Both edit the editor's form through `setForm`; the editor's own save sends
 * them (it spreads the whole form). Neither renders for any other row, and a
 * row without the fields saves exactly as before.
 */
import React from 'react';
import { Check, CreditCard, Layers } from 'lucide-react';
import {
  type CardSetupPolicy, type ReaderCapabilities, type ReaderKind, type Role,
  policyOf, setPolicyRole, KIND_LABEL, CAPABILITY_FIELDS, DEFAULT_READER_CAPABILITIES, readerKindOf,
} from '../../../components/pos/cardSetup/cardSetupModel';

type SetForm = (fn: (f: any) => any) => void;

const Box: React.FC<{ on: boolean; label: string; disabled?: boolean; onChange: (v: boolean) => void }> = ({ on, label, disabled, onChange }) => (
  <button type="button" role="checkbox" aria-checked={on} aria-label={label} disabled={disabled}
    onClick={() => onChange(!on)}
    className="grid h-11 w-11 place-items-center rounded-lg disabled:cursor-not-allowed disabled:opacity-40">
    <span className={`grid h-5 w-5 place-items-center rounded border ${on ? 'border-ruby bg-ruby text-white' : 'border-gray-300 bg-white'}`}>
      {on && <Check size={13} strokeWidth={3} />}
    </span>
  </button>
);

const DefaultPill: React.FC<{ on: boolean; label: string; disabled?: boolean; onClick: () => void }> = ({ on, label, disabled, onClick }) => (
  <button type="button" aria-pressed={on} aria-label={label} disabled={disabled || on} onClick={onClick}
    className={`min-h-[44px] rounded-full px-2 text-[10.5px] font-semibold uppercase tracking-wide ${on ? 'text-ruby-deep' : 'text-gray-400 hover:text-gray-700'} disabled:cursor-default`}>
    <span className={`rounded-full px-2 py-0.5 ${on ? 'bg-[#FFF1F3] ring-1 ring-rose-200' : ''}`}>{on ? 'Default' : 'Make default'}</span>
  </button>
);

const variantLabel = (p: CardSetupPolicy | null) =>
  !p ? '' : p.variant === 'single' ? 'Single screen' : `Dual screen${p.readers.some((r) => r.kind === 'tap_to_pay' && r.enabled) ? ' · NFC' : ''}`;

export const CardSetupPolicyPanel: React.FC<{
  form: any; allModels: Array<{ modelId: string; modelName: string; cardSetupPolicy?: unknown }>;
  setForm: SetForm; buildLabel?: string | null;
}> = ({ form, allModels, setForm, buildLabel }) => {
  const policy = policyOf(form);
  if (!policy) return null;
  const put = (next: CardSetupPolicy) => setForm((f: any) => ({ ...f, cardSetupPolicy: next }));
  const variants = allModels.filter((m) => policyOf(m)?.profile === policy.profile);
  const builtIn = Object.entries((form.appProfile?.capabilities || {}) as Record<string, string>)
    .filter(([, v]) => v === 'builtin').map(([k]) => k.replace(/([A-Z])/g, ' $1').toLowerCase());

  const toggle = (kind: ReaderKind, role: Role, on: boolean) => put(setPolicyRole(policy, kind, role, on));
  const fact = (k: string, v: React.ReactNode) => (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{k}</div>
      <div className="text-[12.5px] font-medium text-gray-800">{v}</div>
    </div>
  );

  return (
    <section className="dq-card p-4" data-testid="card-setup-policy">
      <h3 className="dq-sechead mb-1 flex items-center gap-2"><Layers size={14} /> Card setup</h3>
      <p className="mb-3 text-[11px] text-gray-500">Which card readers a till of this model may use, and what a new till starts with.</p>

      {variants.length > 0 && (
        <div className="mb-3">
          <label className="mb-1 block text-xs font-semibold text-gray-700">Variants</label>
          <div className="flex flex-wrap gap-2">
            {variants.map((v) => {
              const cur = v.modelId === form.modelId;
              return (
                <span key={v.modelId} className={`inline-flex min-h-[36px] items-center rounded-full border px-3 text-[12px] ${cur ? 'border-ruby bg-[#FFF1F3] font-semibold text-ruby-deep' : 'border-gray-200 bg-white text-gray-700'}`}>
                  {variantLabel(policyOf(v))} <span className="ml-1 text-gray-400">· {v.modelName}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {fact('App build', buildLabel || form.buildTarget || '—')}
        {fact('Built-in', builtIn.length ? builtIn.join(', ') : '—')}
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Customer display</div>
            <div className="text-[12.5px] font-medium text-gray-800">{policy.customerDisplay ? 'Yes' : 'No'}</div>
          </div>
          <Box on={policy.customerDisplay} label="Customer display" onChange={(v) => put({ ...policy, customerDisplay: v })} />
        </div>
      </div>

      <label className="mb-1 block text-xs font-semibold text-gray-700">Card setup policy</label>
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full text-[12.5px]">
          <thead className="bg-gray-50 text-[10.5px] font-semibold uppercase tracking-wide text-gray-500">
            <tr><th className="px-3 py-2 text-left">Reader</th><th className="px-2 py-2 text-left">Primary</th><th className="px-2 py-2 text-left">Backup</th></tr>
          </thead>
          <tbody>
            {policy.readers.map((r) => (
              <tr key={r.kind} data-testid={`policy-row-${r.kind}`} className="border-t border-gray-100">
                <td className="px-3 py-1">
                  <span className={r.enabled ? 'font-medium text-gray-800' : 'text-gray-400'}>{KIND_LABEL[r.kind]}</span>
                  {!r.enabled && <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Coming next</span>}
                </td>
                {(['primary', 'backup'] as Role[]).map((role) => {
                  const isDefault = role === 'primary' ? policy.defaultPrimary === r.kind : policy.defaultBackup === r.kind;
                  return (
                    <td key={role} className="px-2 py-1">
                      <div className="flex items-center">
                        <Box on={r[role]} label={`${KIND_LABEL[r.kind]} as ${role}`} disabled={!r.enabled} onChange={(v) => toggle(r.kind, role, v)} />
                        {r.enabled && r[role] && (
                          <DefaultPill on={isDefault} label={`${KIND_LABEL[r.kind]} default ${role}`}
                            onClick={() => put(role === 'primary' ? { ...policy, defaultPrimary: r.kind } : { ...policy, defaultBackup: r.kind })} />
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="border-t border-gray-100" data-testid="policy-row-none">
              <td className="px-3 py-1 font-medium text-gray-800">No backup</td>
              <td className="px-2 py-1 text-gray-300">—</td>
              <td className="px-2 py-1">
                <div className="flex items-center">
                  <Box on={policy.allowNoBackup} label="Allow no backup"
                    onChange={(v) => put({ ...policy, allowNoBackup: v, ...(!v && policy.defaultBackup === 'none' ? { defaultBackup: policy.readers.find((x) => x.enabled && x.backup)?.kind || 'none' } : {}) })} />
                  {policy.allowNoBackup && (
                    <DefaultPill on={policy.defaultBackup === 'none'} label="No backup default" onClick={() => put({ ...policy, defaultBackup: 'none' })} />
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-gray-500">
        Every till of this model is limited to these readers. A new till starts on the defaults; changing them never moves a till that is already set up.
      </p>
    </section>
  );
};

/** The reader family a catalog payment-device row describes, from its lane. */
export function catalogReaderKind(form: any): ReaderKind | null {
  const cls = form?.paymentConfigId || form?.paymentProfile?.deviceClass || '';
  if (!cls || cls === 'none') return null;
  return readerKindOf({ deviceClass: cls, model: form?.modelName, rtpayCardEngine: /tap to pay/i.test(String(form?.modelName || '')) ? 'tap_to_pay' : undefined });
}

export const ReaderCapabilitiesPanel: React.FC<{ form: any; setForm: SetForm }> = ({ form, setForm }) => {
  if (form?.category !== 'payment_device') return null;
  const kind = catalogReaderKind(form);
  const fallback: ReaderCapabilities | null = kind ? DEFAULT_READER_CAPABILITIES[kind] : null;
  const own: ReaderCapabilities | null = form.readerCapabilities && typeof form.readerCapabilities === 'object' ? form.readerCapabilities : null;
  const cur: ReaderCapabilities | null = own || fallback;
  const put = (next: ReaderCapabilities) => setForm((f: any) => ({ ...f, readerCapabilities: next }));
  const empty: ReaderCapabilities = { credit: false, pinDebit: false, tap: false, dip: false, swipe: false, screen: false, tipOnScreen: false, signatureOnScreen: false };
  return (
    <section className="dq-card p-4" data-testid="reader-capabilities">
      <h3 className="dq-sechead mb-1 flex items-center gap-2"><CreditCard size={14} /> Reader capabilities</h3>
      <p className="mb-3 text-[11px] text-gray-500">
        {own
          ? 'Set for this model. Checkout uses these to decide what a till with this reader can offer.'
          : kind
            ? `Using the defaults for ${KIND_LABEL[kind]}. Change any to set them for this model.`
            : 'No reader family for this lane yet — set what this reader can do.'}
      </p>
      <div className="grid grid-cols-2 gap-x-3 sm:grid-cols-4">
        {CAPABILITY_FIELDS.map(({ key, label }) => (
          <div key={key} className="flex items-center gap-1">
            <Box on={!!cur?.[key]} label={label} onChange={(v) => put({ ...(cur || empty), [key]: v })} />
            <span className="text-[12.5px] text-gray-800">{label}</span>
          </div>
        ))}
      </div>
      {own && fallback && JSON.stringify(own) !== JSON.stringify(fallback) && (
        <button type="button" onClick={() => put(fallback)}
          className="mt-2 min-h-[44px] rounded-lg px-2 text-xs font-medium text-ruby hover:text-ruby-deep">
          Use the {KIND_LABEL[kind!]} defaults
        </button>
      )}
    </section>
  );
};
